<script setup lang="ts">
import type { Component } from "vue";
import { computed } from "vue";

import ShortcutStrokes from "~/components/dumb/ShortcutStrokes.vue";
import Template from "~/components/dumb/Template.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { getStrokesForPlatform } from "~/constants/command";
import { formatEmojiName } from "~/constants/iconAndEmoji";
import { CheckIcon, ChevronRightIcon } from "~/icons";
import type { CommandId } from "~/shared/enums";
import { Placement } from "~/shared/enums";
import { usePageStore } from "~/stores";

import { copyAndNotify } from "../notifications";

const props = defineProps<{
  title: string;
  titleClass?: string;
  icon?: Component;
  iconArgs?: object;
  iconAfter?: Component;
  iconAfterArgs?: object;
  selected?: boolean;
  disabled?: boolean;
  subtitle?: string;
  subtitleCopyDescription?: string;
  strikethrough?: boolean;
  commandId?: CommandId;
  rightText?: string | number;
  rightTextStyle?: string;
  isSubmenu?: boolean;
  isMultiSelect?: boolean;
  grid?: boolean;
  emoji?: boolean;
  dataTestid?: string;
  showIconAfterOnHover?: boolean;
}>();

const pageStore = usePageStore();

const strokes = computed(() => (props.commandId ? getStrokesForPlatform(props.commandId, pageStore.isMac) : undefined));

const onClick = (event: Event) => {
  if (pageStore.isMobile || !props.isSubmenu) {
    return;
  }
  event.stopPropagation();
};

const onClickSubtitle = (event: Event) => {
  if (!props.subtitle || !props.subtitleCopyDescription) {
    return;
  }
  event.stopPropagation();
  event.preventDefault();
  copyAndNotify(props.subtitleCopyDescription, props.subtitle);
};
</script>

<template>
  <div
   
    class="flex w-full max-w-full items-center"
    :class="{
      'py-1 pl-3 pr-2': !isMultiSelect && !grid,
      'h-full': grid,
    }"
    @click="onClick"
    @keydown.enter="onClick">
    <component
      :is="icon"
      v-if="icon"
      class="mr-2 shrink-0"
      :class="[
        !iconArgs && `${pageStore.isMobile ? 'icon-md' : 'icon-sm'} ${selected ? 'text-primary-base' : 'text-lt'}`,
        disabled ? 'opacity-50' : '',
      ]"
      v-bind="iconArgs" />

    <div class="flex flex-col overflow-hidden" :class="grid ? 'w-full items-center' : 'mr-2'">
      <component :is="emoji ? Tooltip : Template" :text="formatEmojiName(title)" :placement="Placement.BOTTOM">
        <div
          :title="!emoji ? title : undefined"
          class="select-none truncate"
          :class="[
            titleClass,
            {
              'text-vlt': disabled,
              'text-md': isMultiSelect && !disabled,
              'line-through': strikethrough,
              'text-base': pageStore.isMobile && !emoji,
              'text-sm': !pageStore.isMobile && !emoji,
              'font-emoji text-xl/6': emoji,
            },
          ]">
          {{ title }}
        </div>
      </component>
      <div
        v-if="subtitle"
        :title="subtitle"
        class="flex-1 select-none truncate text-lt"
        :class="pageStore.isMobile ? 'text-sm' : 'text-xs'"
        @click="onClickSubtitle"
        @keydown.enter="onClickSubtitle">
        {{ subtitle }}
      </div>
    </div>

    <div class="flex flex-1 items-center justify-end gap-2">
      <ShortcutStrokes :strokes="strokes" :disabled="!!disabled" />
      <component
        :is="iconAfter"
        v-if="iconAfter"
        v-bind="iconAfterArgs"
        :class="showIconAfterOnHover ? 'hidden group-hover/menu-item:block' : ''" />
      <div
        v-if="rightText"
        class="flex items-center justify-center"
        :class="[
          rightTextStyle
            ? rightTextStyle
            : 'min-h-4 min-w-4 rounded bg-primary-base px-[3px] text-[10px]/[13px] font-semibold text-oncolor',
        ]">
        {{ rightText }}
      </div>
      <ChevronRightIcon v-if="isSubmenu" class="text-vlt" :class="pageStore.isMobile ? 'icon-md' : 'icon-sm'" />
      <CheckIcon v-if="pageStore.isMobile && isMultiSelect && selected" class="text-primary-base icon-md" />
    </div>
  </div>
</template>
